import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import {
  initFirebase,
  onAuthStateChanged,
  signInWithGoogle,
  signInWithEmail,
  registerWithEmail,
  signOut,
  resetPassword,
  type User,
} from "@/lib/firebase";
import { apiFetch } from "@/lib/api";

interface UserProfile {
  id: string;
  email: string | null;
  displayName: string | null;
  photoUrl: string | null;
  stripeCustomerId: string | null;
}

interface AuthContextValue {
  /** Firebase user object, null if not signed in */
  firebaseUser: User | null;
  /** DB user profile, null if not signed in */
  userProfile: UserProfile | null;
  loading: boolean;
  signInGoogle: () => Promise<void>;
  signInEmail: (email: string, password: string) => Promise<void>;
  registerEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const syncProfile = useCallback(async (user: User) => {
    try {
      const profile = await apiFetch<UserProfile>("/api/auth/me", {
        method: "POST",
        body: JSON.stringify({ photoUrl: user.photoURL }),
      });
      setUserProfile(profile);
    } catch {
      // fallback: set minimal profile
      setUserProfile({
        id: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoUrl: user.photoURL,
        stripeCustomerId: null,
      });
    }
  }, []);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    initFirebase()
      .then(({ auth }) => {
        unsubscribe = onAuthStateChanged(auth, async (user) => {
          setFirebaseUser(user);
          if (user) {
            await syncProfile(user);
          } else {
            setUserProfile(null);
          }
          setLoading(false);
        });
      })
      .catch((err) => {
        console.error("Firebase init failed:", err);
        setLoading(false);
      });
    return () => unsubscribe?.();
  }, [syncProfile]);

  const handleAuthError = (err: unknown) => {
    const msg = err instanceof Error ? err.message : "Authentication failed";
    setError(
      msg.includes("auth/wrong-password") || msg.includes("auth/user-not-found")
        ? "Invalid email or password"
        : msg.includes("auth/email-already-in-use")
          ? "An account with this email already exists"
          : msg.includes("auth/popup-closed-by-user")
            ? "Sign-in was cancelled"
            : msg.includes("auth/network-request-failed")
              ? "Network error — please check your connection"
              : "Sign-in failed. Please try again.",
    );
  };

  const signInGoogle = async () => {
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      handleAuthError(err);
    }
  };

  const signInEmailHandler = async (email: string, password: string) => {
    setError(null);
    try {
      await signInWithEmail(email, password);
    } catch (err) {
      handleAuthError(err);
    }
  };

  const registerEmailHandler = async (email: string, password: string) => {
    setError(null);
    try {
      await registerWithEmail(email, password);
    } catch (err) {
      handleAuthError(err);
    }
  };

  const logout = async () => {
    await signOut();
    setUserProfile(null);
  };

  const forgotPassword = async (email: string) => {
    setError(null);
    try {
      await resetPassword(email);
    } catch (err) {
      handleAuthError(err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        userProfile,
        loading,
        signInGoogle,
        signInEmail: signInEmailHandler,
        registerEmail: registerEmailHandler,
        logout,
        forgotPassword,
        error,
        clearError: () => setError(null),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
